import React, { FC } from 'react';

import { Props } from 'react-select';
import {
  languages,
  AvailableLanguages,
  ExtendedDrupalNode,
  sendBeaconEvent,
  BeaconEventMarketingPageElementLocation,
  BeaconEventMarketingPageElementType
} from '@lucid/utils';

import StyledSelect from './styled-select';
import SelectCustomOption from './select-custom-option';

export interface SelectWrapperSwitcherValues {
  /** The label that will be displayed on the select options component. */
  label: string;
  /** The value for the label in the select options component. */
  value: string;
}

export interface LanguageSwitcherProps extends Props {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Hides the languages in the select option. */
  disabledLanguages?: AvailableLanguages[];
  /** The entity values to allow for customization of urls. */
  entityTranslations: ExtendedDrupalNode[];
  /** The short language prefix to create proper urls. */
  shortLangPrefix: string | AvailableLanguages;
}

const LanguageSwitcher: FC<LanguageSwitcherProps> = ({
  disabledLanguages,
  entityTranslations,
  shortLangPrefix
}) => {
  const showLangSelect = !disabledLanguages?.includes(shortLangPrefix as AvailableLanguages);
  const switcherValues = Object.keys(languages)
    .filter(lang => !disabledLanguages?.includes(lang as AvailableLanguages))
    .reduce<Record<string, SelectWrapperSwitcherValues>>((obj, langPrefix) => {
      const { shortLangPrefix } = languages[
        langPrefix
      ];
      const { entityUrl } =
        (entityTranslations || []).find(
          ({ status, entityLanguage }) =>
            status && entityLanguage!.id === langPrefix
        ) || {};

      // check for drupal /homepage alias and remove it in the language switcher
      if (entityUrl?.path?.endsWith('/homepage')) {
        entityUrl.path = entityUrl.path.replace('/homepage', '');
      }

      obj[shortLangPrefix] = {
        value: entityUrl?.path
          ? entityUrl.path
          : `/${shortLangPrefix === 'en' ? '' : shortLangPrefix}`,
        label: languages[langPrefix].name
      };

      return obj;
    }, {});

  return showLangSelect ? (
    <StyledSelect
      menuIsOpen
      classNamePrefix="languageSwitcher"
      components={{
        Option: SelectCustomOption
      }}
      isSearchable={false}
      onClick={() =>
        sendBeaconEvent({
          name: 'clickedMarketingPageElement',
          elementLocation: BeaconEventMarketingPageElementLocation.footer,
          elementDetail: 'lang-toggle',
          elementType: BeaconEventMarketingPageElementType.link
        })}
      options={Object.values(switcherValues)}
      value={switcherValues[shortLangPrefix]}
    />
  ) : null;
};

export { LanguageSwitcher };
export default LanguageSwitcher;
